import { useEffect, useMemo, useState } from 'react';
import { Pie } from 'react-chartjs-2';
import { useAdMatchQuery, useAdAllocationsQuery, useSaveAllocationMutation } from '../api/queries';
import { showToast } from '../components/Toast';
import type { AdMatchTerritory } from '../types';

type Props = {
  af: string | null;
};

function formatMoney(val: number | undefined | null): string {
  if (val === null || val === undefined) return 'N/A';
  return Number(val).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

const cardStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 12,
  boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
  padding: '28px 32px',
};

export default function AdMatchTab({ af }: Props) {
  const { data, isLoading, error } = useAdMatchQuery(af);

  if (!af) {
    return <Placeholder />;
  }
  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 48, color: '#8a95a5' }}>
        <div className="spinner" style={{ margin: '0 auto 16px' }} />
        Loading ad match data...
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: 48, color: '#c0392b' }}>
        Error loading ad match data
      </div>
    );
  }
  if (!data || !data.found) {
    return (
      <div style={{ textAlign: 'center', padding: 48, color: '#8a95a5' }}>
        <Icon />
        <h3 style={{ margin: '0 0 8px' }}>No Ad Match Data</h3>
        <p>No ad match information found for {af}</p>
        {data?.source && (
          <p style={{ fontSize: 12, marginTop: 8 }}>
            Source: {data.source} | Updated: {data.lastUpdated ?? 'N/A'}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="admatch-grid">
      {/* ── Box 1: Total campaign spend match ───────────────────────── */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 20px', color: '#1a2a3a', fontSize: 18, fontWeight: 600 }}>
          Paid Campaign Spend Match
        </h3>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 0',
            borderBottom: '1px solid #eef1f5',
          }}
        >
          <div>
            <div style={{ fontSize: 13, color: '#8a95a5', marginBottom: 4 }}>
              Current Match Amount for this Territory
            </div>
            <div style={{ fontSize: 14, color: '#3a4a5a' }}>{af}</div>
          </div>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#27ae60' }}>
            ${formatMoney(data.matchAmount)}
            <span style={{ fontSize: 14, fontWeight: 500, color: '#8a95a5' }}>/mo</span>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 0',
          }}
        >
          <div>
            <div style={{ fontSize: 13, color: '#8a95a5', marginBottom: 4 }}>
              Current Match Amount for All Your Territories
            </div>
            <div style={{ fontSize: 14, color: '#3a4a5a' }}>{data.name}</div>
          </div>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#2980b9' }}>
            ${formatMoney(data.combinedMatch)}
            <span style={{ fontSize: 14, fontWeight: 500, color: '#8a95a5' }}>/mo</span>
          </div>
        </div>
      </div>

      {data.territories && data.territories.length > 1 && (
        <div style={cardStyle}>
          <h3 style={{ margin: '0 0 16px', color: '#1a2a3a', fontSize: 18, fontWeight: 600 }}>
            Territory Breakdown
          </h3>
          {data.territories.map((t, i) => {
            const isLast = i === data.territories!.length - 1;
            return (
              <div
                key={t.afSub}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px 0',
                  borderBottom: isLast ? 'none' : '1px solid #eef1f5',
                }}
              >
                <span style={{ fontSize: 14, color: '#3a4a5a' }}>{t.afSub}</span>
                <span style={{ fontSize: 16, fontWeight: 600, color: '#3a4a5a' }}>
                  ${formatMoney(t.matchAmount)}/mo
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Qualified Lead Program ──────────────────────────────────── */}
      {data.qualifiedLead && (
        <QualifiedLeadCard
          territories={data.territories ?? []}
          program={data.qualifiedLead}
        />
      )}

      {/* ── Paid Campaign Budget Allocation ─────────────────────────── */}
      {data.territories && data.territories.length > 0 && (
        <AllocationCard af={af} territories={data.territories} />
      )}

      <div
        style={{
          gridColumn: '1 / -1',
          textAlign: 'center',
          fontSize: 12,
          color: '#b0b8c4',
          paddingTop: 8,
        }}
      >
        Source: {data.source} | Updated: {data.lastUpdated}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Qualified Lead Program
// ─────────────────────────────────────────────────────────────────────
function QualifiedLeadCard({
  territories,
  program,
}: {
  territories: AdMatchTerritory[];
  program: NonNullable<import('../types').AdMatchResponse['qualifiedLead']>;
}) {
  const pct = Math.round(program.matchRate * 100);
  const multi = territories.length > 1;

  return (
    <div style={cardStyle}>
      <h3 style={{ margin: '0 0 8px', color: '#1a2a3a', fontSize: 18, fontWeight: 600 }}>
        Qualified Lead Program
      </h3>
      <p style={{ margin: '0 0 20px', fontSize: 13.5, color: '#5a6675', lineHeight: 1.5 }}>
        We match <strong>{pct}%</strong> of your A Place for Mom lead cost — currently{' '}
        <strong>${program.perLeadMatch}</strong> of the <strong>${program.leadCost}</strong> per
        lead — up to a monthly lead cap set by each territory&rsquo;s annualized revenue. The
        numbers below are the <strong>maximum leads per month we&rsquo;ll match</strong>, not a lead
        guarantee.
      </p>

      {multi &&
        territories.map((t, i) => (
          <div
            key={t.afSub}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '10px 0',
              borderBottom: i === territories.length - 1 ? '1px solid #eef1f5' : '1px solid #f2f5f8',
            }}
          >
            <span style={{ fontSize: 14, color: '#3a4a5a' }}>{t.afSub}</span>
            <span style={{ fontSize: 14, color: '#3a4a5a' }}>
              <strong>{t.qualifiedLeads ?? 0}</strong> leads/mo matched @ {pct}%
              <span style={{ color: '#8a95a5' }}>
                {' '}
                (up to ${((t.qualifiedLeads ?? 0) * program.perLeadMatch).toLocaleString()}/mo)
              </span>
            </span>
          </div>
        ))}

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: multi ? '16px 0 4px' : '4px 0',
        }}
      >
        <div>
          <div style={{ fontSize: 13, color: '#8a95a5', marginBottom: 4 }}>
            {multi ? 'Total leads matched across your territories' : 'Leads matched each month'}
          </div>
          <div style={{ fontSize: 13, color: '#8a95a5' }}>
            We cover up to ${formatMoney(program.maxMonthlyMatch)}/mo of your lead spend
          </div>
        </div>
        <div style={{ fontSize: 28, fontWeight: 700, color: '#8e44ad' }}>
          {program.totalLeads}
          <span style={{ fontSize: 14, fontWeight: 500, color: '#8a95a5' }}> leads/mo</span>
        </div>
      </div>

      <div
        style={{
          marginTop: 16,
          padding: '14px 16px',
          background: '#f7f3fb',
          borderRadius: 8,
          fontSize: 13.5,
          color: '#3a4a5a',
        }}
      >
        To enroll in the Qualified Lead Program, contact{' '}
        <strong>{program.enrollContactName}</strong> at{' '}
        <a
          href={`mailto:${program.enrollContactEmail}?subject=${encodeURIComponent(
            'Amada Qualified Lead Program enrollment',
          )}`}
          style={{ color: '#8e44ad', fontWeight: 600 }}
        >
          {program.enrollContactEmail}
        </a>
        .
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Paid Campaign Budget Allocation (Google vs Meta, per territory)
// ─────────────────────────────────────────────────────────────────────
function AllocationCard({ af, territories }: { af: string; territories: AdMatchTerritory[] }) {
  const { data: allocData } = useAdAllocationsQuery(af);
  const saveMut = useSaveAllocationMutation();

  // Saved Meta % per territory (default 0 = 100% Google).
  const savedMeta = useMemo(() => {
    const m: Record<string, number> = {};
    for (const a of allocData?.allocations ?? []) m[a.afSub] = a.metaPct;
    return m;
  }, [allocData]);

  // Local (draft) Meta % per territory, seeded from saved values.
  const [draft, setDraft] = useState<Record<string, number>>({});
  useEffect(() => {
    setDraft((prev) => {
      const next = { ...prev };
      for (const t of territories) {
        if (next[t.afSub] === undefined) next[t.afSub] = savedMeta[t.afSub] ?? 0;
      }
      return next;
    });
  }, [territories, savedMeta]);

  const [savingAf, setSavingAf] = useState<string | null>(null);

  const save = (afSub: string) => {
    const metaPct = draft[afSub] ?? 0;
    setSavingAf(afSub);
    saveMut.mutate(
      { afSub, metaPct },
      {
        onSuccess: () =>
          showToast(`Allocation saved for ${afSub} — marketing has been notified.`),
        onError: (e) => showToast(e.message || 'Could not save allocation', { variant: 'error' }),
        onSettled: () => setSavingAf(null),
      },
    );
  };

  return (
    <div style={cardStyle}>
      <h3 style={{ margin: '0 0 8px', color: '#1a2a3a', fontSize: 18, fontWeight: 600 }}>
        Paid Campaign Budget Allocation
      </h3>
      <p style={{ margin: '0 0 8px', fontSize: 13.5, color: '#5a6675', lineHeight: 1.5 }}>
        Choose how to split each territory&rsquo;s paid ad budget between channels. Google Ads is
        the default; move the slider to shift budget toward Meta.
      </p>
      <p
        style={{
          margin: '0 0 20px',
          fontSize: 12.5,
          color: '#8a6d3b',
          background: '#fcf8e3',
          border: '1px solid #faebcc',
          borderRadius: 8,
          padding: '10px 12px',
        }}
      >
        Saving a change emails our marketing team so they can update your allocation in the ad
        accounts. Changes aren&rsquo;t applied automatically.
      </p>

      {territories.map((t, i) => {
        const meta = draft[t.afSub] ?? 0;
        const google = 100 - meta;
        const dirty = meta !== (savedMeta[t.afSub] ?? 0);
        const pieData = {
          labels: ['Google Ads', 'Meta'],
          datasets: [
            {
              data: [google, meta],
              backgroundColor: ['#2980b9', '#8e44ad'],
              borderColor: '#fff',
              borderWidth: 2,
            },
          ],
        };
        return (
          <div
            key={t.afSub}
            style={{
              padding: '18px 0',
              borderTop: i === 0 ? 'none' : '1px solid #eef1f5',
            }}
          >
            <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
              {/* Allocation pie */}
              <div style={{ width: 88, height: 88, flexShrink: 0 }}>
                <Pie data={pieData} options={PIE_OPTS} />
              </div>

              {/* Slider + controls */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    marginBottom: 8,
                  }}
                >
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#3a4a5a' }}>{t.afSub}</span>
                  <span style={{ fontSize: 13, color: '#5a6675' }}>
                    <span style={{ color: '#2980b9', fontWeight: 600 }}>Google {google}%</span>
                    {'  ·  '}
                    <span style={{ color: '#8e44ad', fontWeight: 600 }}>Meta {meta}%</span>
                  </span>
                </div>
                {/* Slider tracks GOOGLE %: full-right (100%) is the default; drag
                    left to give up Google budget in exchange for Meta. */}
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={5}
                  value={google}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, [t.afSub]: 100 - Number(e.target.value) }))
                  }
                  style={{ width: '100%', accentColor: '#2980b9', cursor: 'pointer' }}
                  aria-label={`Google Ads budget percentage for ${t.afSub}`}
                />
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 11,
                    color: '#b0b8c4',
                    marginTop: 2,
                  }}
                >
                  <span>← shift to Meta</span>
                  <span>100% Google Ads →</span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: 10,
                  }}
                >
                  <span style={{ fontSize: 11.5, color: '#b0b8c4' }}>
                    {dirty ? 'Unsaved change' : 'Saved'}
                  </span>
                  <button
                    type="button"
                    disabled={!dirty || savingAf === t.afSub}
                    onClick={() => save(t.afSub)}
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      padding: '6px 16px',
                      borderRadius: 6,
                      border: 'none',
                      cursor: dirty && savingAf !== t.afSub ? 'pointer' : 'default',
                      color: '#fff',
                      background: dirty ? '#8e44ad' : '#cbd2da',
                    }}
                  >
                    {savingAf === t.afSub ? 'Saving…' : 'Save & notify marketing'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })}

      <div
        style={{
          marginTop: 16,
          paddingTop: 14,
          borderTop: '1px dashed #e3e8ee',
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontSize: 12.5, color: '#8a95a5' }}>More channels coming soon:</span>
        <span style={comingSoonPill}>YouTube — coming soon</span>
        <span style={comingSoonPill}>TikTok — coming soon</span>
      </div>
    </div>
  );
}

const PIE_OPTS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false as const },
    tooltip: { callbacks: { label: (c: { label: string; parsed: number }) => `${c.label}: ${c.parsed}%` } },
  },
};

const comingSoonPill: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: '#94a0ad',
  background: '#f2f5f8',
  border: '1px solid #e3e8ee',
  borderRadius: 999,
  padding: '4px 12px',
};

function Placeholder() {
  return (
    <div className="tab-placeholder">
      <Icon />
      <h3>Ad Match Amount(s)</h3>
      <p>Select a franchisee to view their ad match details</p>
    </div>
  );
}

function Icon() {
  return (
    <svg
      width="48"
      height="48"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      style={{ opacity: 0.3, marginBottom: 12 }}
    >
      <path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  );
}
